import React, {
  Fragment,
  useCallback,
  useState,
  useEffect,
  useRef,
} from "react";
import { debounce } from "utils";
import * as Components from "components";
import {
  getConversations,
  getConversationMessages,
  sendMessage,
  markConversationAsRead,
} from "services/intercom.service";
import { SyncIcon, ChevronLeft } from "assets/icons";
import { SendMessage } from "assets/svg";
import { toast } from "react-toastify";
import useThemeStore from "(store)/themeStore";
import chatIcon from "assets/svg/Chat.svg";
import parse from "html-react-parser";
import Socket from "services/socket";

const ChatPage = () => {
  // Refs
  const messageEndRef = useRef(null);
  const conversationListRef = useRef(null);
  const socketRef = useRef(null);
  const selectedConversationRef = useRef(null);

  // Theme
  const theme = useThemeStore((state) => state.theme);
  const isThemeDark = theme === "dark";

  // Mobile state
  const [isMobile, setIsMobile] = useState(false);
  const [showConversationList, setShowConversationList] = useState(true);

  // Core state
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [messageText, setMessageText] = useState("");
  const [search, setSearch] = useState("");

  // Loading states
  const [loading, setLoading] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  // Pagination state
  const [paginationData, setPaginationData] = useState({
    total_count: 0,
    current_page: 1,
    total_pages: 0,
    per_page: 10,
    has_more: false,
    next_cursor: null,
  });

  // Filter state
  const [filter, setFilter] = useState({
    search: "",
    per_page: 10,
  });

  // ================================
  // SOCKET EVENT HANDLERS
  // ================================

  const handleNewConversation = useCallback((data) => {
    console.log("📨 New customer conversation:", data);

    if (!data.conversation?.id) {
      return;
    }

    setConversations((prev) => {
      const exists = prev.find((conv) => conv.id === data.conversation.id);
      if (exists) return prev;

      return [data.conversation, ...prev];
    });

    setPaginationData((prev) => ({
      ...prev,
      total_count: prev.total_count + 1,
    }));

    toast.info(
      `New conversation from ${
        data.conversation.customer?.name || "Unknown Customer"
      }`
    );
  }, []);

  const handleNewMessage = useCallback((data) => {
    if (!data.message || !data.conversation_id) {
      return;
    }

    const currentSelectedConversation = selectedConversationRef.current;
    const isCurrentConversation =
      currentSelectedConversation?.id === data.conversation_id;

    setConversations((prev) => {
      return prev
        .map((conv) => {
          if (conv.id === data.conversation_id) {
            return {
              ...conv,
              updated_at: data.message.created_at,
              read: isCurrentConversation ? true : false,
              conversation_parts: {
                conversation_parts: [
                  ...(conv.conversation_parts?.conversation_parts || []),
                  data.message,
                ],
              },
            };
          }
          return conv;
        })
        .sort((a, b) => b.updated_at - a.updated_at);
    });

    if (isCurrentConversation) {
      setConversationMessages((prev) => {
        const exists = prev.find(
          (msg) =>
            msg.id === data.message.id ||
            (msg.body === data.message.body &&
              Math.abs(msg.created_at - data.message.created_at) < 2)
        );

        if (exists) {
          return prev;
        }

        // Create the new message object with consistent structure
        const newMessage = {
          id: data.message.id,
          body: data.message.body,
          author: data.message.author,
          created_at: data.message.created_at,
          part_type: data.message.part_type || "comment",
        };

        const newMessages = [...prev, newMessage];
        return newMessages.sort((a, b) => a.created_at - b.created_at);
      });

      setTimeout(() => {
        messageEndRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      }, 100);
    } else {
      const customerName =
        data.conversation?.customer?.name || "Unknown Customer";
    }
  }, []);

  const handleConversationAssigned = useCallback((data) => {
    setConversations((prev) =>
      prev.map((conv) =>
        conv.id === data.conversation_id
          ? { ...conv, assignee: data.assignee }
          : conv
      )
    );
  }, []);

  const handleStatusChange = useCallback((data) => {
    setConversations((prev) =>
      prev.map((conv) =>
        conv.id === data.conversation_id
          ? {
              ...conv,
              open: data.status === "opened",
              state: data.conversation.state,
            }
          : conv
      )
    );

    const currentSelected = selectedConversationRef.current;
    if (currentSelected?.id === data.conversation_id) {
      setSelectedConversation((prev) => ({
        ...prev,
        open: data.status === "opened",
        state: data.conversation.state,
      }));
    }
  }, []);

  // ================================
  // SOCKET INITIALIZATION
  // ================================

  useEffect(() => {
    socketRef.current = Socket();
    const socket = socketRef.current;

    socket.emit("adminOnline", {
      token: localStorage.getItem("token"),
      userType: "admin",
    });

    socket.on("connect", () => {
      console.log("✅ Socket connected successfully");
    });

    socket.on("disconnect", () => {
      console.log("❌ Socket disconnected");
    });

    socket.on("connect_error", (error) => {
      console.error("🔥 Socket connection error:", error);
    });

    socket.on("admin:new_customer_conversation", handleNewConversation);
    socket.on("admin:new_customer_message", handleNewMessage);
    socket.on("admin:conversation_assigned", handleConversationAssigned);
    socket.on("admin:conversation_status_changed", handleStatusChange);

    socket.on("error", (error) => {
      console.error("🔥 Socket error:", error);
    });

    return () => {
      if (socketRef.current) {
        console.log("🧹 Cleaning up socket listeners");
        socket.off("connect");
        socket.off("disconnect");
        socket.off("connect_error");
        socket.off("admin:new_customer_conversation");
        socket.off("admin:new_customer_message");
        socket.off("admin:conversation_assigned");
        socket.off("admin:conversation_status_changed");
        socket.off("error");
      }
    };
  }, [
    handleNewConversation,
    handleNewMessage,
    handleConversationAssigned,
    handleStatusChange,
  ]);

  // ================================
  // SELECTED CONVERSATION REF SYNC
  // ================================

  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);

  // ================================
  // SOCKET ROOM MANAGEMENT
  // ================================

  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return;

    if (selectedConversation?.id) {
      socket.emit("joinIntercomConversation", {
        conversationId: selectedConversation.id,
      });

      socket.emit("markIntercomAsRead", {
        conversationId: selectedConversation.id,
      });

      return () => {
        socket.emit("leaveIntercomConversation", {
          conversationId: selectedConversation.id,
        });
      };
    }
  }, [selectedConversation?.id]);

  // ================================
  // RESPONSIVE & MOBILE HANDLING
  // ================================

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 1024; // lg breakpoint
      setIsMobile(mobile);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    if (isMobile && selectedConversation) {
      setShowConversationList(false);
    }
  }, [isMobile, selectedConversation]);

  // ================================
  // PAGINATION & SCROLL HANDLING
  // ================================

  useEffect(() => {
    const conversationList = conversationListRef.current;
    if (!conversationList) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = conversationList;
      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

      if (
        scrollPercentage > 0.9 &&
        paginationData.has_more &&
        !loadingMore &&
        !loading &&
        conversations.length < paginationData.total_count
      ) {
        handleLoadMore();
      }
    };

    conversationList.addEventListener("scroll", handleScroll);
    return () => conversationList.removeEventListener("scroll", handleScroll);
  }, [
    paginationData.has_more,
    paginationData.total_count,
    loadingMore,
    loading,
    conversations.length,
  ]);

  // ================================
  // GENERAL EFFECTS
  // ================================

  useEffect(() => {
    if (error) {
      toast.error(error);
      setError(null);
    }
  }, [error]);

  useEffect(() => {
    fetchConversations(true);
  }, [filter.search]);

  useEffect(() => {
    if (selectedConversation?.id) {
      setConversationMessages([]);
      setLoadingMessages(true);
      setError(null);

      const timeoutId = setTimeout(() => {
        fetchConversationMessages(selectedConversation.id);
      }, 50);

      return () => clearTimeout(timeoutId);
    }
  }, [selectedConversation?.id]);

  useEffect(() => {
    if (
      messageEndRef.current &&
      conversationMessages.length > 0 &&
      !loadingMessages &&
      selectedConversation
    ) {
      setTimeout(() => {
        messageEndRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      }, 150);
    }
  }, [conversationMessages, loadingMessages, selectedConversation]);

  // ================================
  // SEARCH HANDLING
  // ================================

  const debouncedSearch = useCallback(
    debounce((value) => {
      setFilter((prev) => ({
        ...prev,
        search: value,
      }));
    }, 500),
    []
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    debouncedSearch(value);
  };

  // ================================
  // API FUNCTIONS
  // ================================

  const fetchConversations = async (reset = false) => {
    if (reset) {
      setLoading(true);
      setConversations([]);
    } else {
      setLoadingMore(true);
    }

    setError(null);

    try {
      const queryParams = {
        ...filter,
        starting_after: reset ? "" : paginationData.next_cursor || "",
      };

      const response = await getConversations(queryParams);

      const responseData = response.data.response || response.data;
      const conversationsData =
        responseData.data || responseData.conversations || [];
      const pages = responseData.pages || {};
      const totalCount = responseData.total_count || 0;

      if (reset) {
        setConversations(conversationsData);
        setSelectedConversation(null);
      } else {
        setConversations((prev) => [...prev, ...conversationsData]);
      }

      setPaginationData({
        total_count: totalCount,
        current_page: pages.page || 1,
        total_pages: pages.total_pages || 0,
        per_page: parseInt(pages.per_page) || 10,
        has_more: !!pages.next && conversationsData.length > 0,
        next_cursor: pages.next?.starting_after || null,
      });
    } catch (err) {
      console.error("Error fetching conversations:", err);
      setError("Failed to fetch conversations. Please try again.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (
      paginationData.has_more &&
      !loadingMore &&
      conversations.length < paginationData.total_count
    ) {
      fetchConversations(false);
    }
  };

  const fetchConversationMessages = async (conversationId) => {
    if (loadingMessages && selectedConversation?.id === conversationId) return;

    setLoadingMessages(true);
    setError(null);

    try {
      const response = await getConversationMessages(conversationId);
      const conversation = response.data;

      if (selectedConversation?.id !== conversationId) {
        setLoadingMessages(false);
        return;
      }

      const allMessages = [];

      if (
        conversation.source &&
        conversation.source.body &&
        conversation.source.body !== "No content"
      ) {
        allMessages.push({
          id: "source",
          body: conversation.source.body,
          author: conversation.source.author,
          created_at: conversation.created_at,
        });
      }

      if (conversation.conversation_parts?.conversation_parts) {
        const validParts =
          conversation.conversation_parts.conversation_parts.filter(
            (part) => part.body && part.body !== "No content"
          );
        allMessages.push(...validParts);
      }

      allMessages.sort((a, b) => a.created_at - b.created_at);
      setConversationMessages(allMessages);
    } catch (err) {
      console.error("Error fetching conversation messages:", err);
      if (selectedConversation?.id === conversationId) {
        setError("Failed to fetch conversation messages. Please try again.");
      }
    } finally {
      if (selectedConversation?.id === conversationId) {
        setLoadingMessages(false);
      }
    }
  };

  // ================================
  // USER INTERACTION HANDLERS
  // ================================

  const handleConversationSelect = (conversation) => {
    if (selectedConversation?.id === conversation.id) return;

    setConversationMessages([]);
    setLoadingMessages(false);
    setSelectedConversation(conversation);
    setError(null);
    setMessageText("");

    if (isMobile) {
      setShowConversationList(false);
    }

    if (!conversation.read) {
      markAsRead(conversation.id);
    }
  };

  const handleBackToConversations = () => {
    setShowConversationList(true);
    if (isMobile) {
      setSelectedConversation(null);
    }
  };

  const markAsRead = async (conversationId) => {
    try {
      await markConversationAsRead(conversationId);
      setConversations(
        conversations.map((conv) =>
          conv.id === conversationId ? { ...conv, read: true } : conv
        )
      );
    } catch (err) {
      console.error(
        `Error marking conversation ${conversationId} as read:`,
        err
      );
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageText.trim() || !selectedConversation || sending) return;

    const currentConversationId = selectedConversation.id;
    const messageContent = messageText.trim();
    setSending(true);

    const optimisticMessage = {
      id: `temp-${Date.now()}`,
      body: messageContent,
      author: {
        type: "admin",
        name: "You",
      },
      created_at: Math.floor(Date.now() / 1000),
      part_type: "comment",
    };

    setConversationMessages((prev) => [...prev, optimisticMessage]);
    setMessageText("");

    setTimeout(() => {
      messageEndRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }, 50);

    try {
      const response = await sendMessage(currentConversationId, messageContent);

      setConversationMessages((prev) =>
        prev.filter((msg) => msg.id !== optimisticMessage.id)
      );

      const realMessage = {
        id: `admin-${Date.now()}`,
        body: messageContent,
        author: {
          type: "admin",
          name: "You",
        },
        created_at: Math.floor(Date.now() / 1000),
        part_type: "comment",
      };

      setConversationMessages((prev) => {
        const exists = prev.find(
          (msg) =>
            msg.body === messageContent &&
            msg.author?.type === "admin" &&
            Math.abs(msg.created_at - realMessage.created_at) < 5
        );

        if (exists) return prev;

        return [...prev, realMessage].sort(
          (a, b) => a.created_at - b.created_at
        );
      });

      setConversations((prev) =>
        prev
          .map((conv) =>
            conv.id === currentConversationId
              ? {
                  ...conv,
                  updated_at: Math.floor(Date.now() / 1000),
                  conversation_parts: {
                    conversation_parts: [
                      ...(conv.conversation_parts?.conversation_parts || []),
                      realMessage,
                    ],
                  },
                }
              : conv
          )
          .sort((a, b) => b.updated_at - a.updated_at)
      );

      setTimeout(() => {
        messageEndRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      }, 100);
    } catch (err) {
      setConversationMessages((prev) =>
        prev.filter((msg) => msg.id !== optimisticMessage.id)
      );
      setMessageText(messageContent); // Restore message text
      setError("Failed to send message. Please try again.");
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  // ================================
  // HELPER FUNCTIONS
  // ================================

  const formatTime = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp * 1000);
    const now = new Date();
    const diffInDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) {
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    } else if (diffInDays === 1) {
      return "Yesterday";
    } else if (diffInDays < 7) {
      return date.toLocaleDateString([], { weekday: "short" });
    } else {
      return date.toLocaleDateString();
    }
  };

  const getLastMessage = (conversation) => {
    if (!conversation.conversation_parts?.conversation_parts?.length) {
      return {
        body: conversation.source?.body || "",
        created_at: conversation.created_at,
        author: conversation.source?.author,
      };
    }

    const parts = conversation.conversation_parts.conversation_parts;
    return parts[parts.length - 1];
  };

  const getPreviewText = (text) => {
    if (!text) return "No message";

    // Check for image patterns
    const imageMatch =
      text.match(/\[Image\s+"([^"]+)"\]/) ||
      text.match(/<img\s+[^>]*src=["'][^"']*["'][^>]*>/i);
    const gifMatch = text.match(/\[GIF\]/);

    if (imageMatch) {
      return (
        <div className="space-y-2">
          <div className="text-gray-500 text-sm">📷 Image</div>
        </div>
      );
    }

    if (gifMatch) {
      return (
        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
          <span className="text-lg">🎬</span>
          <span>GIF</span>
        </div>
      );
    }

    return text.length > 60
      ? parse(text.substring(0, 60)) + "..."
      : parse(text);
  };

  const getCustomerInfo = (conversation) => {
    return {
      name: conversation.source?.author?.name || "Unknown Customer",
      email: conversation.source?.author?.email || "No email",
    };
  };

  const renderMessageContent = (messageBody) => {
    if (!messageBody) return "No content";

    const imageMatch = messageBody.match(/\[Image\s+"([^"]+)"\]/);
    const gifMatch = messageBody.match(/\[GIF\]/);

    if (imageMatch) {
      const imageUrl = imageMatch[1];
      return (
        <div className="space-y-2">
          <img
            src={imageUrl}
            alt="Shared image"
            className="max-w-full h-auto rounded-lg shadow-sm"
            onError={(e) => {
              e.target.style.display = "none";
              e.target.nextSibling.style.display = "block";
            }}
          />
          <div className="hidden text-gray-500 text-sm">
            📷 Image (failed to load)
          </div>
        </div>
      );
    }

    if (gifMatch) {
      return (
        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
          <span className="text-lg">🎬</span>
          <span>GIF</span>
        </div>
      );
    }

    try {
      return <div className="message-content">{parse(messageBody)}</div>;
    } catch (error) {
      // Fallback to plain text if parsing fails
      return messageBody;
    }
  };

  // ================================
  // RENDER FUNCTIONS
  // ================================

  const renderConversationsList = () => (
    <div
      className={`${
        isMobile
          ? showConversationList
            ? "flex flex-col"
            : "hidden"
          : "lg:col-span-1"
      } 
      ${
        isMobile
          ? "absolute inset-0 z-40 bg-white dark:bg-bgprimary"
          : "border-r dark:border-gray-800 h-full overflow-hidden flex flex-col"
      }`}
    >
      {isMobile && (
        <div className="flex items-center justify-between p-4 border-b dark:border-gray-800 flex-shrink-0">
          <h2 className="font-semibold text-lg">Conversations</h2>
          {selectedConversation && (
            <button
              onClick={() => setShowConversationList(false)}
              className="btn btn-sm btn-ghost"
            >
              Back to Chat
            </button>
          )}
        </div>
      )}

      {/* Desktop header */}
      {!isMobile && (
        <div className="py-3 px-4 bg-gray-50 dark:bg-bgprimary border-b dark:border-gray-800 flex-shrink-0">
          <h2 className="font-semibold text-sm text-secondary dark:text-alpha">
            Conversations ({paginationData.total_count})
          </h2>
        </div>
      )}

      <div
        ref={conversationListRef}
        data-conversation-list
        className="flex-1 overflow-y-auto overflow-x-hidden"
        style={{
          scrollBehavior: "auto",
          WebkitOverflowScrolling: "touch",
          overscrollBehavior: "contain",
          scrollbarWidth: "thin",
          // scrollbarColor: "#9CA3AF transparent",
          // minHeight: "200px",
        }}
      >
        {loading ? (
          <div className="flex flex-col items-center justify-center h-full min-h-[200px]">
            <div className="loading loading-spinner loading-md text-secondary"></div>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Loading conversations...
            </p>
          </div>
        ) : conversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full min-h-[200px] text-gray-500 dark:text-gray-400">
            <div>
              <img src={chatIcon} alt="Chat Icon" className="w-full h-1/3" />
              <p className="text-xl font-bold text-gray-700 text-center">
                No conversations found
              </p>
            </div>
          </div>
        ) : (
          <div className="divide-y dark:divide-gray-800">
            {conversations.map((conversation) => {
              const lastMessage = getLastMessage(conversation);
              const customer = getCustomerInfo(conversation);

              return (
                <div
                  key={conversation.id}
                  onClick={() => handleConversationSelect(conversation)}
                  className={`p-3 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors ${
                    selectedConversation?.id === conversation.id &&
                    "bg-gray-100 dark:bg-gray-800 border-r-2 border-secondary"
                  }`}
                  style={{
                    borderTopWidth: "0px",
                  }}
                >
                  <div className="flex justify-between items-start mb-1">
                    <div className="font-medium flex items-center gap-2 flex-1 min-w-0">
                      <span className="truncate font-semibold">
                        {customer.name}
                      </span>
                      {!conversation.read && (
                        <span className="w-2 h-2 rounded-full bg-secondary flex-shrink-0"></span>
                      )}
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0 ml-2">
                      {formatTime(conversation.updated_at)}
                    </span>
                  </div>

                  <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                    {lastMessage && (
                      <span>
                        {lastMessage.author?.type === "admin"
                          ? "You: "
                          : lastMessage.author?.type === "bot"
                          ? "Bot: "
                          : ""}
                        {getPreviewText(lastMessage.body)}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}

            {loadingMore &&
              conversations.length < paginationData.total_count && (
                <div className="p-4 text-center">
                  <div className="loading loading-spinner loading-sm text-secondary"></div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Loading more conversations...
                  </p>
                </div>
              )}

            {(!paginationData.has_more ||
              conversations.length >= paginationData.total_count) &&
              conversations.length > 0 && (
                <div className="p-4 text-center">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    All conversations loaded
                  </p>
                </div>
              )}
          </div>
        )}
      </div>
    </div>
  );

  const renderChatArea = () => (
    <div
      className={`${
        isMobile
          ? showConversationList
            ? "hidden"
            : "flex flex-col"
          : "lg:col-span-2 flex flex-col"
      } 
      h-full overflow-hidden ${
        isMobile ? "absolute inset-0 z-30 bg-white dark:bg-bgprimary" : ""
      }`}
    >
      {!selectedConversation ? (
        <div className="flex flex-col items-center justify-center h-full min-h-[200px] text-gray-500 dark:text-gray-400 p-4">
          <div>
            <img src={chatIcon} alt="Chat Icon" className="w-full h-1/3" />
            <p className="text-xl font-bold text-gray-700 text-center">
              Select a chat to start messaging
            </p>
          </div>
          {isMobile && (
            <button
              onClick={handleBackToConversations}
              className="btn bg-secondary text-white btn-sm mt-4"
            >
              View Conversations
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="py-4 px-5 border-b dark:border-gray-800 flex justify-between items-center bg-white dark:bg-bgsecondary flex-shrink-0">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {isMobile && (
                <button
                  onClick={handleBackToConversations}
                  className="btn btn-sm btn-ghost p-1 flex-shrink-0"
                >
                  <ChevronLeft size={20} />
                </button>
              )}

              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">
                  {getCustomerInfo(selectedConversation).name}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                  {getCustomerInfo(selectedConversation).email}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              {selectedConversation.open ? (
                <span className="badge bg-secondary text-white border-none text-xs">
                  Open
                </span>
              ) : (
                <span className="badge bg-gray-500 text-white border-none text-xs">
                  Closed
                </span>
              )}
              {selectedConversation.source?.delivered_as && (
                <span className="hidden sm:inline text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded">
                  via{" "}
                  {selectedConversation.source.delivered_as.replace(/_/g, " ")}
                </span>
              )}
            </div>
          </div>

          <div
            className="flex-grow overflow-y-auto overflow-x-hidden p-3 sm:p-5 bg-gray-50 dark:bg-bgprimary messages-scroll"
            style={{
              scrollBehavior: "auto",
              WebkitOverflowScrolling: "touch",
              overscrollBehavior: "contain",
              scrollbarWidth: "thin",
              scrollbarColor: "#CBD5E0 transparent",
            }}
          >
            {loadingMessages ? (
              <div className="flex flex-col items-center justify-center h-full min-h-[200px]">
                <div className="loading loading-spinner loading-md text-secondary"></div>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  Loading messages...
                </p>
              </div>
            ) : conversationMessages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full min-h-[200px] text-gray-500 dark:text-gray-400">
                <p>No messages in this conversation</p>
              </div>
            ) : (
              <div className="space-y-4">
                {conversationMessages.map((message, index) => {
                  const isAdmin = message.author?.type === "admin";
                  const isBot = message.author?.type === "bot";
                  const isOptimistic = message.id
                    ?.toString()
                    .startsWith("temp-");

                  return (
                    <div
                      key={`${selectedConversation?.id || "default"}-${
                        message.id
                      }-${index}`}
                      className={`flex ${
                        isAdmin ? "justify-end" : "justify-start"
                      } ${isOptimistic ? "opacity-70" : ""}`}
                    >
                      <div
                        className={`max-w-[85%] sm:max-w-[80%] ${
                          isAdmin ? "text-right" : "text-left"
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                          {isAdmin ? (
                            <>
                              <span>{formatTime(message.created_at)}</span>
                              <span className="font-medium">
                                {message.author?.name || "Support"}
                              </span>
                              {isOptimistic && (
                                <span className="text-xs text-orange-500">
                                  Sending...
                                </span>
                              )}
                            </>
                          ) : (
                            <>
                              <span className="font-medium">
                                {message.author?.name || "Customer"}
                              </span>
                              <span>{formatTime(message.created_at)}</span>
                            </>
                          )}
                        </div>
                        <div
                          className={`py-2 sm:py-3 px-3 sm:px-4 rounded-lg break-words text-sm sm:text-base ${
                            isAdmin
                              ? "bg-secondary text-white rounded-tr-none"
                              : "bg-white dark:bg-bgsecondary border dark:border-gray-700 rounded-tl-none"
                          }`}
                        >
                          {renderMessageContent(message.body)}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div
                  ref={messageEndRef}
                  style={{ height: "1px", visibility: "hidden" }}
                  aria-hidden="true"
                />
              </div>
            )}
          </div>

          <div className="border-t dark:border-gray-800 p-3 sm:p-4 bg-white dark:bg-bgsecondary flex-shrink-0">
            <form
              onSubmit={handleSendMessage}
              className="flex items-center gap-2 sm:gap-3"
            >
              <input
                type="text"
                className={`flex w-full px-3 py-2 border border-gray-300 placeholder-gray-500 rounded-lg focus:outline-none text-sm sm:text-base
                ${
                  isThemeDark
                    ? "bg-slate-950 focus-within:text-black text-gray-300 focus-within:bg-white"
                    : "bg-white text-gray-900"
                }
                focus:z-10`}
                placeholder={
                  selectedConversation.open
                    ? "Type your message..."
                    : "This conversation is closed"
                }
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                disabled={sending || !selectedConversation.open}
              />
              <button
                type="submit"
                className={`p-2 rounded-lg transition-colors ${
                  sending || !messageText.trim() || !selectedConversation.open
                    ? "text-gray-400"
                    : "text-secondary dark:text-alpha hover:bg-gray-100 dark:hover:bg-gray-700"
                }`}
                disabled={
                  sending || !messageText.trim() || !selectedConversation.open
                }
              >
                {sending ? (
                  <span className="loading loading-spinner loading-sm"></span>
                ) : (
                  <SendMessage />
                )}
              </button>
            </form>
            {!selectedConversation.open && (
              <div className="text-xs text-center mt-2 text-gray-500 dark:text-gray-400">
                This conversation is closed. You can reopen it from the ticket
                management page.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );

  // ================================
  // MAIN RENDER
  // ================================

  return (
    <Fragment>
      <style jsx>{`
        /* Enhanced scrollbar visibility */

        [data-conversation-list]::-webkit-scrollbar-track {
          background: transparent;
        }

        [data-conversation-list]::-webkit-scrollbar-thumb {
          background-color: #9ca3af !important;
        }

        [data-conversation-list]::-webkit-scrollbar-thumb:hover {
          background-color: #6b7280;
        }

        /* Ensure proper scrolling on all devices */
        [data-conversation-list] {
          scrollbar-color: #9ca3af transparent !important;
        }

        /* Messages area scrollbar */
        .messages-scroll::-webkit-scrollbar {
          width: 6px;
        }

        .messages-scroll::-webkit-scrollbar-thumb {
          background-color: #cbd5e0;
          border-radius: 3px;
        }

        /* Ensure proper scrolling on mobile */
        @media (max-width: 1024px) {
          [data-conversation-list] {
            -webkit-overflow-scrolling: touch !important;
            overflow-scrolling: touch !important;
          }
        }
      `}</style>

      <div className="flex flex-col border dark:border-gray-800 my-2 sm:my-5 2xl:my-10 mx-2 sm:mx-5 2xl:mx-14 rounded-lg shadow-lg dark:bg-bgprimary">
        {/* Header - Hidden on mobile when chat is open */}
        <div
          className={`${
            isMobile && !showConversationList ? "hidden" : ""
          } w-full flex items-center px-3 sm:px-5 py-3 border-b dark:border-gray-800`}
        >
          <div className="w-full flex flex-col sm:flex-row gap-3 sm:gap-5">
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-5 items-stretch sm:items-center w-full xl:w-2/3">
              <div className="w-full xl:w-[400px]">
                <Components.SearchField
                  mobilePlaceholder="Search"
                  placeholder="Search by customer name, email or last message"
                  value={search}
                  onChange={handleSearchChange}
                />
              </div>
            </div>
            <div className="flex items-center justify-center sm:justify-end xl:w-1/3">
              <button
                onClick={() => fetchConversations(true)}
                className="btn btn-outline btn-sm gap-2 w-full sm:w-auto"
                disabled={loading}
              >
                <SyncIcon /> Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div
          className={`${
            isMobile ? "relative" : "grid grid-cols-1 lg:grid-cols-3"
          }`}
          style={{
            height: isMobile ? "calc(100vh - 140px)" : "calc(100vh - 200px)",
          }}
        >
          {renderConversationsList()}
          {renderChatArea()}
        </div>

        {/* Footer - Hidden on mobile when chat is open */}
        <div
          className={`${
            isMobile && !showConversationList ? "hidden" : ""
          } flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 my-3 px-3 sm:px-5 py-2 border-t dark:border-gray-800`}
        >
          <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
            {paginationData.total_count > 0 && (
              <span>
                Showing{" "}
                {Math.min(conversations.length, paginationData.total_count)} of{" "}
                {paginationData.total_count} conversations
                {paginationData.total_pages > 0 && (
                  <span className="hidden sm:inline">
                    {" "}
                    • Page {paginationData.current_page} of{" "}
                    {paginationData.total_pages}
                  </span>
                )}
              </span>
            )}
          </div>
          <div className="flex items-center gap-4">
            {(!paginationData.has_more ||
              conversations.length >= paginationData.total_count) &&
              conversations.length > 0 && (
                <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                  All conversations loaded
                </span>
              )}
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ChatPage;
